import { createHttpService } from "../ApiService";
import {
  TCreateInventoryLinkRequest,
  TCreateLessonPlanRequest,
  TCreateSessionRequest,
  TCreateUnitRequest,
  TInventoryLinkDeleteAck,
  TInventoryLinkMutationResponse,
  TLessonPlan,
  TLessonPlanEnvelope,
  TLessonPlanInventoryLink,
  TLessonPlanInventorySnapshot,
  TLessonPlanListFilters,
  TLessonPlanListResponse,
  TLessonPlanMutationAck,
  TLessonPlanSession,
  TLessonPlanUnit,
  TLessonPlanVersionRef,
  TSessionDeleteAck,
  TSessionMutationResponse,
  TUnitDeleteAck,
  TUnitMutationResponse,
  TUpdateInventoryLinkRequest,
  TUpdateLessonPlanRequest,
  TUpdateSessionRequest,
  TUpdateUnitRequest,
} from "./lessonPlan.interfaces";
import { lessonPlanListMock } from "../../__mocks__/lessonPlan/lessonPlanMock";

const buildPaginatedMock = (
  filters: TLessonPlanListFilters | undefined,
): TLessonPlanListResponse => {
  const f = filters ?? {};
  // Match BE behavior: archived rows hidden by default unless ?status=archived (or any
  // explicit status) is passed.
  const filtered = lessonPlanListMock.filter((plan) => {
    if (!f.status && plan.status === "archived") return false;
    if (f.instituteId && plan.institute?.id !== f.instituteId) return false;
    if (f.classId && plan.class?.id !== f.classId) return false;
    if (f.courseId && plan.course?.id !== f.courseId) return false;
    if (f.teacherId && plan.teacher?.id !== f.teacherId) return false;
    if (f.academicYear && plan.academicYear !== f.academicYear) return false;
    if (f.term && plan.term !== f.term) return false;
    if (f.status && plan.status !== f.status) return false;
    if (f.search) {
      const q = f.search.toLowerCase();
      const haystack = [
        plan.title,
        plan.description ?? "",
        plan.subject ?? "",
        plan.gradeLevel ?? "",
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const page = f.page ?? 1;
  const limit = f.limit ?? 20;
  const start = (page - 1) * limit;
  const data = filtered.slice(start, start + limit);
  return { data, count: filtered.length };
};

const findMockPlan = (id: string): TLessonPlan => {
  const plan = lessonPlanListMock.find((p) => p.id === id);
  if (!plan) throw new Error("Lesson plan not found");
  return plan;
};

const bumpMockPlan = (plan: TLessonPlan): TLessonPlanVersionRef => {
  plan.version = (plan.version ?? 1) + 1;
  plan.updatedAt = new Date().toISOString();
  return { id: plan.id, version: plan.version, updatedAt: plan.updatedAt };
};

const findMockUnit = (
  plan: TLessonPlan,
  unitId: string,
): TLessonPlanUnit => {
  const unit = plan.units?.find((u) => u.id === unitId);
  if (!unit) throw new Error("Unit not found");
  return unit;
};

const inventorySnapshot = (
  inventoryId: string,
  override?: Partial<TLessonPlanInventorySnapshot>,
): TLessonPlanInventorySnapshot => ({
  id: inventoryId,
  name: override?.name ?? `Inventory ${inventoryId.slice(0, 6)}`,
  type: override?.type ?? "consumable",
  quantity: override?.quantity ?? 0,
});

export const useLessonPlanService = () => {
  const httpAPI = createHttpService();
  const basePath = "/lesson-plans";

  const appWithMockData = import.meta.env.VITE_APP_WITH_MOCK_DATA === "true";
  const shouldUseMock = appWithMockData;

  const getLessonPlans = (filters?: TLessonPlanListFilters) => {
    if (shouldUseMock) {
      return Promise.resolve(buildPaginatedMock(filters));
    }
    return httpAPI.academicsRoot.get<TLessonPlanListResponse>(basePath, {
      params: filters,
    });
  };

  // BE default for ?includeUnits is `false`. The handler has no ParseBoolPipe yet,
  // so `?includeUnits=false` is incorrectly truthy. Workaround: omit the param when
  // false; only append `?includeUnits=true` when units are wanted.
  const getLessonPlanById = (id: string, includeUnits = true) => {
    if (shouldUseMock) {
      return Promise.resolve(findMockPlan(id));
    }
    return httpAPI.academicsRoot.get<TLessonPlan>(`${basePath}/${id}`, {
      params: includeUnits ? { includeUnits: true } : undefined,
    });
  };

  // POST /lesson-plans body wraps under `{ lessonPlan }`. Unlike PATCH/DELETE/archive
  // and the 9 child routes, this endpoint has not yet migrated to @InstituteId();
  // Joi still requires lessonPlan.instituteId or the request 400s. So pass the full
  // request through (including instituteId) until BE migrates it.
  const createLessonPlan = (request: TCreateLessonPlanRequest) => {
    if (shouldUseMock) {
      const now = new Date().toISOString();
      const newPlan: TLessonPlan = {
        id: `lesson-plan-${Date.now()}`,
        title: request.title,
        description: request.description,
        academicYear: request.academicYear,
        term: request.term,
        board: request.board,
        class: request.classId ? { id: request.classId } : undefined,
        course: request.courseId ? { id: request.courseId } : undefined,
        teacher: request.teacherId ? { id: request.teacherId } : undefined,
        curriculumFramework: request.curriculumFramework,
        syllabusReference: request.syllabusReference,
        textbookReference: request.textbookReference,
        ncertChapter: request.ncertChapter,
        subject: request.subject,
        gradeLevel: request.gradeLevel,
        tags: request.tags,
        status: request.status ?? "draft",
        totalDuration: request.totalDuration,
        version: 1,
        institute: request.instituteId
          ? { id: request.instituteId }
          : { id: "institute-1" },
        units: [],
        inventory: [],
        createdAt: now,
        updatedAt: now,
      };
      lessonPlanListMock.push(newPlan);
      return Promise.resolve(newPlan);
    }
    return httpAPI.academicsRoot
      .post<TLessonPlanEnvelope>(basePath, { lessonPlan: request })
      .then((envelope) => envelope.lessonPlan);
  };

  const updateLessonPlan = (id: string, request: TUpdateLessonPlanRequest) => {
    if (shouldUseMock) {
      const existing = findMockPlan(id);
      Object.assign(existing, {
        title: request.title ?? existing.title,
        description: request.description ?? existing.description,
        academicYear: request.academicYear ?? existing.academicYear,
        term: request.term ?? existing.term,
        board: request.board ?? existing.board,
        class:
          request.classId !== undefined
            ? request.classId
              ? { id: request.classId }
              : null
            : existing.class,
        course:
          request.courseId !== undefined
            ? request.courseId
              ? { id: request.courseId }
              : null
            : existing.course,
        teacher:
          request.teacherId !== undefined
            ? request.teacherId
              ? { id: request.teacherId }
              : null
            : existing.teacher,
        curriculumFramework:
          request.curriculumFramework ?? existing.curriculumFramework,
        syllabusReference:
          request.syllabusReference ?? existing.syllabusReference,
        textbookReference:
          request.textbookReference ?? existing.textbookReference,
        ncertChapter: request.ncertChapter ?? existing.ncertChapter,
        subject: request.subject ?? existing.subject,
        gradeLevel: request.gradeLevel ?? existing.gradeLevel,
        tags: request.tags ?? existing.tags,
        status: request.status ?? existing.status,
        totalDuration: request.totalDuration ?? existing.totalDuration,
      });
      bumpMockPlan(existing);
      return Promise.resolve(existing);
    }
    return httpAPI.academicsRoot
      .patch<TLessonPlanEnvelope>(`${basePath}/${id}`, { lessonPlan: request })
      .then((envelope) => envelope.lessonPlan);
  };

  const deleteLessonPlan = (id: string) => {
    if (shouldUseMock) {
      const index = lessonPlanListMock.findIndex((p) => p.id === id);
      if (index === -1) {
        return Promise.reject(new Error("Lesson plan not found"));
      }
      lessonPlanListMock.splice(index, 1);
      return Promise.resolve<TLessonPlanMutationAck>({
        success: true,
        message: "Lesson plan deleted",
      });
    }
    return httpAPI.academicsRoot.delete<TLessonPlanMutationAck>(
      `${basePath}/${id}`,
    );
  };

  const archiveLessonPlan = (id: string) => {
    if (shouldUseMock) {
      const existing = findMockPlan(id);
      existing.status = "archived";
      bumpMockPlan(existing);
      return Promise.resolve(existing);
    }
    return httpAPI.academicsRoot
      .post<TLessonPlanEnvelope>(`${basePath}/${id}/archive`)
      .then((envelope) => envelope.lessonPlan);
  };

  // -------------------------------------------------------------------------
  // Units
  // -------------------------------------------------------------------------

  const createUnit = (lessonPlanId: string, request: TCreateUnitRequest) => {
    if (shouldUseMock) {
      const plan = findMockPlan(lessonPlanId);
      if (
        plan.units?.some((u) => u.unitNumber === request.unitNumber)
      ) {
        return Promise.reject(
          new Error(
            `Unit number ${request.unitNumber} already exists in this plan`,
          ),
        );
      }
      const now = new Date().toISOString();
      const newUnit: TLessonPlanUnit = {
        id: `unit-${Date.now()}`,
        lessonPlanId: plan.id,
        unitNumber: request.unitNumber,
        title: request.title,
        description: request.description,
        duration: request.duration,
        objectives: request.objectives,
        outcomes: request.outcomes,
        assessments: request.assessments,
        differentiationStrategies: request.differentiationStrategies,
        resources: request.resources,
        documentIds: request.documentIds,
        sessions: [],
        createdAt: now,
        updatedAt: now,
      };
      plan.units = [...(plan.units ?? []), newUnit];
      const lessonPlan = bumpMockPlan(plan);
      return Promise.resolve<TUnitMutationResponse>({ unit: newUnit, lessonPlan });
    }
    return httpAPI.academicsRoot.post<TUnitMutationResponse>(
      `${basePath}/${lessonPlanId}/units`,
      { unit: request },
    );
  };

  const updateUnit = (
    lessonPlanId: string,
    unitId: string,
    request: TUpdateUnitRequest,
  ) => {
    if (shouldUseMock) {
      const plan = findMockPlan(lessonPlanId);
      const unit = findMockUnit(plan, unitId);
      if (
        request.unitNumber !== undefined &&
        request.unitNumber !== unit.unitNumber &&
        plan.units?.some((u) => u.unitNumber === request.unitNumber)
      ) {
        return Promise.reject(
          new Error(
            `Unit number ${request.unitNumber} already exists in this plan`,
          ),
        );
      }
      Object.assign(unit, request, { updatedAt: new Date().toISOString() });
      const lessonPlan = bumpMockPlan(plan);
      return Promise.resolve<TUnitMutationResponse>({ unit, lessonPlan });
    }
    return httpAPI.academicsRoot.patch<TUnitMutationResponse>(
      `${basePath}/${lessonPlanId}/units/${unitId}`,
      { unit: request },
    );
  };

  const deleteUnit = (lessonPlanId: string, unitId: string) => {
    if (shouldUseMock) {
      const plan = findMockPlan(lessonPlanId);
      const idx = plan.units?.findIndex((u) => u.id === unitId) ?? -1;
      if (idx === -1) return Promise.reject(new Error("Unit not found"));
      plan.units!.splice(idx, 1);
      const lessonPlan = bumpMockPlan(plan);
      return Promise.resolve<TUnitDeleteAck>({
        success: true,
        message: "Unit deleted",
        unitId,
        lessonPlan,
      });
    }
    return httpAPI.academicsRoot.delete<TUnitDeleteAck>(
      `${basePath}/${lessonPlanId}/units/${unitId}`,
    );
  };

  // -------------------------------------------------------------------------
  // Sessions
  // -------------------------------------------------------------------------

  const createSession = (
    lessonPlanId: string,
    unitId: string,
    request: TCreateSessionRequest,
  ) => {
    if (shouldUseMock) {
      const plan = findMockPlan(lessonPlanId);
      const unit = findMockUnit(plan, unitId);
      if (
        unit.sessions?.some((s) => s.sessionNumber === request.sessionNumber)
      ) {
        return Promise.reject(
          new Error(
            `Session number ${request.sessionNumber} already exists in this unit`,
          ),
        );
      }
      const now = new Date().toISOString();
      const newSession: TLessonPlanSession = {
        id: `session-${Date.now()}`,
        unitId: unit.id,
        sessionNumber: request.sessionNumber,
        title: request.title,
        description: request.description,
        duration: request.duration,
        activities: request.activities,
        materialsRequired: request.materialsRequired,
        homework: request.homework,
        notes: request.notes,
        createdAt: now,
        updatedAt: now,
      };
      unit.sessions = [...(unit.sessions ?? []), newSession];
      unit.updatedAt = now;
      const lessonPlan = bumpMockPlan(plan);
      return Promise.resolve<TSessionMutationResponse>({
        session: newSession,
        lessonPlan,
      });
    }
    return httpAPI.academicsRoot.post<TSessionMutationResponse>(
      `${basePath}/${lessonPlanId}/units/${unitId}/sessions`,
      { session: request },
    );
  };

  const updateSession = (
    lessonPlanId: string,
    unitId: string,
    sessionId: string,
    request: TUpdateSessionRequest,
  ) => {
    if (shouldUseMock) {
      const plan = findMockPlan(lessonPlanId);
      const unit = findMockUnit(plan, unitId);
      const session = unit.sessions?.find((s) => s.id === sessionId);
      if (!session) return Promise.reject(new Error("Session not found"));
      if (
        request.sessionNumber !== undefined &&
        request.sessionNumber !== session.sessionNumber &&
        unit.sessions?.some(
          (s) => s.sessionNumber === request.sessionNumber,
        )
      ) {
        return Promise.reject(
          new Error(
            `Session number ${request.sessionNumber} already exists in this unit`,
          ),
        );
      }
      Object.assign(session, request, { updatedAt: new Date().toISOString() });
      unit.updatedAt = session.updatedAt;
      const lessonPlan = bumpMockPlan(plan);
      return Promise.resolve<TSessionMutationResponse>({ session, lessonPlan });
    }
    return httpAPI.academicsRoot.patch<TSessionMutationResponse>(
      `${basePath}/${lessonPlanId}/units/${unitId}/sessions/${sessionId}`,
      { session: request },
    );
  };

  const deleteSession = (
    lessonPlanId: string,
    unitId: string,
    sessionId: string,
  ) => {
    if (shouldUseMock) {
      const plan = findMockPlan(lessonPlanId);
      const unit = findMockUnit(plan, unitId);
      const idx = unit.sessions?.findIndex((s) => s.id === sessionId) ?? -1;
      if (idx === -1) return Promise.reject(new Error("Session not found"));
      unit.sessions!.splice(idx, 1);
      unit.updatedAt = new Date().toISOString();
      const lessonPlan = bumpMockPlan(plan);
      return Promise.resolve<TSessionDeleteAck>({
        success: true,
        message: "Session deleted",
        sessionId,
        lessonPlan,
      });
    }
    return httpAPI.academicsRoot.delete<TSessionDeleteAck>(
      `${basePath}/${lessonPlanId}/units/${unitId}/sessions/${sessionId}`,
    );
  };

  // -------------------------------------------------------------------------
  // Inventory links
  // -------------------------------------------------------------------------

  const createInventoryLink = (
    lessonPlanId: string,
    request: TCreateInventoryLinkRequest,
  ) => {
    if (shouldUseMock) {
      const plan = findMockPlan(lessonPlanId);
      if (
        plan.inventory?.some((i) => i.inventoryId === request.inventoryId)
      ) {
        return Promise.reject(
          new Error("Inventory item already linked to this plan"),
        );
      }
      const now = new Date().toISOString();
      const newLink: TLessonPlanInventoryLink = {
        id: `inv-link-${Date.now()}`,
        lessonPlanId: plan.id,
        inventoryId: request.inventoryId,
        quantityRequired: request.quantityRequired,
        notes: request.notes,
        inventory: inventorySnapshot(request.inventoryId),
        createdAt: now,
        updatedAt: now,
      };
      plan.inventory = [...(plan.inventory ?? []), newLink];
      const lessonPlan = bumpMockPlan(plan);
      return Promise.resolve<TInventoryLinkMutationResponse>({
        inventoryLink: newLink,
        lessonPlan,
      });
    }
    return httpAPI.academicsRoot.post<TInventoryLinkMutationResponse>(
      `${basePath}/${lessonPlanId}/inventory`,
      { inventoryLink: request },
    );
  };

  const updateInventoryLink = (
    lessonPlanId: string,
    inventoryLinkId: string,
    request: TUpdateInventoryLinkRequest,
  ) => {
    if (shouldUseMock) {
      const plan = findMockPlan(lessonPlanId);
      const link = plan.inventory?.find((i) => i.id === inventoryLinkId);
      if (!link) return Promise.reject(new Error("Inventory link not found"));
      Object.assign(link, request, { updatedAt: new Date().toISOString() });
      const lessonPlan = bumpMockPlan(plan);
      return Promise.resolve<TInventoryLinkMutationResponse>({
        inventoryLink: link,
        lessonPlan,
      });
    }
    return httpAPI.academicsRoot.patch<TInventoryLinkMutationResponse>(
      `${basePath}/${lessonPlanId}/inventory/${inventoryLinkId}`,
      { inventoryLink: request },
    );
  };

  const deleteInventoryLink = (
    lessonPlanId: string,
    inventoryLinkId: string,
  ) => {
    if (shouldUseMock) {
      const plan = findMockPlan(lessonPlanId);
      const idx =
        plan.inventory?.findIndex((i) => i.id === inventoryLinkId) ?? -1;
      if (idx === -1)
        return Promise.reject(new Error("Inventory link not found"));
      plan.inventory!.splice(idx, 1);
      const lessonPlan = bumpMockPlan(plan);
      return Promise.resolve<TInventoryLinkDeleteAck>({
        success: true,
        message: "Inventory link deleted",
        inventoryLinkId,
        lessonPlan,
      });
    }
    return httpAPI.academicsRoot.delete<TInventoryLinkDeleteAck>(
      `${basePath}/${lessonPlanId}/inventory/${inventoryLinkId}`,
    );
  };

  return {
    getLessonPlans,
    getLessonPlanById,
    createLessonPlan,
    updateLessonPlan,
    deleteLessonPlan,
    archiveLessonPlan,
    createUnit,
    updateUnit,
    deleteUnit,
    createSession,
    updateSession,
    deleteSession,
    createInventoryLink,
    updateInventoryLink,
    deleteInventoryLink,
  };
};
